import { c as _c } from "react/compiler-runtime";
import { keepPreviousData } from '@tanstack/react-query';
import { InferenceAlgorithm } from '@/services/bayesianApi/models';
import { VariableTypeSelectEntry } from '@/services/db/schema';
import { NetworkStatus } from '@/services/models/enums';
import { Arrow, Variable } from '@/types/variable';
import convertVariablesAndTypesIntoNetwork from '@/utils/conversions/convertVariablesAndTypesIntoNetwork';
import { trpc } from '@/utils/trpc';
import { useNetworkLoadErrorEffect } from './useNetworkLoadErrorEffect';
export type Network = {
  id: string;
  name: string;
  // inferenceAlgorithm is nullable and not optional because of "incompatibility" between drizzle and zod
  // there must be a way to be able solve it somehow but couldn't find it
  inferenceAlgorithm: InferenceAlgorithm;
  status: NetworkStatus;
  isMain: boolean;
  isReleaseBranch: boolean;
  // KBE-769: the default every semi-continuous variable with no custom
  // `transitionFunctions` inherits. Carried on the processed network so the
  // digraph editor and the diagnosis page can resolve it without a second query.
  defaultTransitionHalfBandPercent: number;
  variables: Variable[];
  variableById: Map<string, Variable>;
  variableIds: Set<string>;
  arrows: Map<string, Arrow>;
  variableTypes: VariableTypeSelectEntry[];
  lastModification?: Date;
};
export type UseNetworkProps = {
  networkId: string | undefined;
};

/**
 * Invalidates and refetches the network with the given networkId.
 * @param options.cancelAndRefetchAnyCurrentRequest - If true and the network is
 *   currently being fetched, the current fetch will be canceled and the network
 *   will be refetched. Be careful with this option, as it will result in
 *   existing refetch promises being resolved, which can lead to pending states
 *   or optimistic updates being discarded if they are waiting for the refetch
 *   to complete. If optimistic updates or pending states are removed too early,
 *   it may result in a flickering UI that can cause flakiness in E2E tests.
 *   The option should be set to true only when we need to guarantee that the
 *   latest data is fetched, because sometimes the current request may return
 *   outdated data.
 * @default options.cancelAndRefetchAnyCurrentRequest false
 */
export const useInvalidateNetwork = (options?: {
  cancelAndRefetchAnyCurrentRequest: boolean;
}) => {
  const cancelRefetch = options ? options.cancelAndRefetchAnyCurrentRequest : false;
  const utils = trpc.useUtils();
  return async (networkId: string | undefined) => await utils.network.getNetwork.invalidate({
    networkId
  }, undefined, {
    cancelRefetch
  });
};
export const useNetwork = t0 => {
  const $ = _c(8);
  const {
    networkId
  } = t0;
  const t1 = networkId ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = {
      networkId: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !!networkId;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      enabled: t3,
      placeholderData: keepPreviousData,
      select: _temp
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    data: network,
    error,
    refetch,
    isLoading
  } = trpc.network.getNetwork.useQuery(t2, t4);
  useNetworkLoadErrorEffect(error);
  let t5;
  if ($[4] !== isLoading || $[5] !== network || $[6] !== refetch) {
    t5 = {
      refetch,
      network,
      isLoading
    };
    $[4] = isLoading;
    $[5] = network;
    $[6] = refetch;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
export default useNetwork;
function _temp(networkWithVariablesAndTypes) {
  return convertVariablesAndTypesIntoNetwork({
    ...networkWithVariablesAndTypes
  });
}